import React from "react";
import Parse from "parse";

import { useTranslation } from "@opendash/core";
import {
  Button,
  Modal,
  Space,
  Divider,
  Upload,
  Pagination,
  message,
  Input,
  Form,
  Tabs,
} from "antd";
import { Icon } from "@opendash/icons";

import { InvoicePage } from "../types";

import * as PDFJS from "pdfjs-dist";

import type { RcFile } from "antd/lib/upload";
import type { PDFDocumentProxy } from "pdfjs-dist/types/display/api";

// TODO
// PDFJS.GlobalWorkerOptions.workerSrc =
//   "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.9.359/pdf.worker.js";
PDFJS.GlobalWorkerOptions.workerSrc = "/pdfjs-worker.js";

interface Props {
  visible: boolean;
  invoicePage: InvoicePage | null;
  onClose(): void;
  onChange(): void;
}

export const InvoicePageImageReplace = React.memo<Props>(
  function InvoicePageImageReplace({
    visible,
    invoicePage,
    onClose,
    onChange,
  }) {
    const t = useTranslation();
    const [form] = Form.useForm();

    const previewContainerRef = React.useRef<HTMLCanvasElement>(null);

    const [file, setFile] = React.useState<RcFile | null>(null);
    const [imageFile, setImageFile] = React.useState<RcFile | null>(null);
    const [pdf, setPDF] = React.useState<PDFDocumentProxy | null>(null);
    const [page, setPage] = React.useState(0);
    const [pageCount, setPageCount] = React.useState(0);
    const [isUploading, setIsUploading] = React.useState(false);
    const [activeTab, setActiveTab] = React.useState("pdf");
    const [imagePreviewUrl, setImagePreviewUrl] = React.useState<string | null>(
      null
    );

    React.useEffect(() => {
      if (!visible) {
        setFile(null);
        setImageFile(null);
        setIsUploading(false);
        setActiveTab("pdf");
        // Clean up preview URL
        if (imagePreviewUrl) {
          URL.revokeObjectURL(imagePreviewUrl);
          setImagePreviewUrl(null);
        }
      } else if (invoicePage) {
        // Set form values when modal opens
        form.setFieldsValue({
          title: invoicePage.get("title") || "",
          description: invoicePage.get("description") || "",
        });
      }
    }, [visible, invoicePage, form, imagePreviewUrl]);

    React.useEffect(() => {
      if (file && previewContainerRef.current) {
        const fileReader = new FileReader();

        fileReader.onload = function () {
          //@ts-ignore
          PDFJS.getDocument(new Uint8Array(fileReader.result)).promise.then(
            (pdf) => {
              setPDF(pdf);
            }
          );
        };

        fileReader.readAsArrayBuffer(file);
      } else {
        setPDF(null);
      }
    }, [file]);

    React.useEffect(() => {
      if (pdf && pdf.numPages > 0) {
        setPage(1);
        setPageCount(pdf.numPages);
      } else {
        setPage(0);
        setPageCount(0);
      }
    }, [pdf]);

    React.useEffect(() => {
      if (pdf && page > 0 && previewContainerRef.current) {
        pdf.getPage(page).then((page) => {
          const scale = 3;
          const viewport = page.getViewport({ scale });

          const canvas = previewContainerRef.current as HTMLCanvasElement;
          const context = canvas.getContext("2d") as CanvasRenderingContext2D;

          canvas.height = viewport.height;
          canvas.width = viewport.width;

          page.render({
            canvasContext: context,
            viewport: viewport,
          });
        });
      } else if (previewContainerRef.current) {
        previewContainerRef.current.width = 0;
        previewContainerRef.current.height = 0;
      }
    }, [pdf, page]);

    const saveChanges = React.useCallback(async () => {
      if (!invoicePage) {
        return;
      }

      try {
        const values = await form.validateFields();
        setIsUploading(true);

        // Update title and description
        invoicePage.set("title", values.title);
        invoicePage.set("description", values.description);

        // If there's a PDF page selected, update the image from PDF
        if (
          activeTab === "pdf" &&
          pdf &&
          page > 0 &&
          previewContainerRef.current
        ) {
          const dataUrl = previewContainerRef.current.toDataURL("image/jpeg");

          const image = new Parse.File(
            `pdf-replace-page-${page}.jpg`,
            { base64: dataUrl },
            "image/png"
          );

          await image.save();
          invoicePage.set("image", image);
        }

        // If there's an image file selected, update the image from file
        if (activeTab === "image" && imageFile) {
          const image = new Parse.File(imageFile.name, imageFile);
          await image.save();
          invoicePage.set("image", image);
        }

        await invoicePage.save();

        message.success(t("app:admin.invoice_page_image_replace.save_success"));
        onChange();
        onClose();
      } catch (error) {
        message.error(t("app:admin.invoice_page_image_replace.save_error"));
      } finally {
        setIsUploading(false);
      }
    }, [form, invoicePage, pdf, page, onChange, onClose, t]);

    return (
      <Modal
        title={t("app:admin.invoice_page_image_replace.title")}
        visible={visible}
        onCancel={onClose}
        width={1000}
        footer={[
          <Button key="cancel" onClick={onClose}>
            {t("app:admin.invoice_page_image_replace.action_cancel")}
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={isUploading}
            onClick={saveChanges}
          >
            {t("app:admin.invoice_page_image_replace.action_save")}
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="title"
            label={t("app:admin.invoice_page_image_replace.field_title")}
            rules={[
              {
                required: true,
                message: t(
                  "app:admin.invoice_page_image_replace.field_title_required"
                ),
              },
            ]}
          >
            <Input
              placeholder={t(
                "app:admin.invoice_page_image_replace.field_title_placeholder"
              )}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label={t("app:admin.invoice_page_image_replace.field_description")}
            extra={t(
              "app:admin.invoice_page_image_replace.field_description_hint"
            )}
          >
            <Input.TextArea
              rows={3}
              placeholder={t(
                "app:admin.invoice_page_image_replace.field_description_placeholder"
              )}
            />
          </Form.Item>
        </Form>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "pdf",
              label: (
                <span>
                  <Icon icon="fa:file-pdf" />
                  {t("app:admin.invoice_page_image_replace.tab_pdf")}
                </span>
              ),
              children: (
                <div>
                  <Space direction="horizontal" style={{ marginBottom: 16 }}>
                    <Upload
                      fileList={[]}
                      accept=".pdf"
                      beforeUpload={(file) => {
                        setFile(file);
                        return false;
                      }}
                    >
                      <Button
                        icon={<Icon icon="fa:file-pdf" />}
                        children={
                          file
                            ? t(
                                "app:admin.invoice_page_pdf_import.action_upload_new"
                              )
                            : t(
                                "app:admin.invoice_page_pdf_import.action_upload"
                              )
                        }
                      />
                    </Upload>

                    {pageCount > 0 && (
                      <>
                        <Divider type="vertical" />
                        <Pagination
                          current={page}
                          pageSize={1}
                          total={pageCount}
                          showSizeChanger={false}
                          onChange={(page) => {
                            setPage(page);
                          }}
                        />
                      </>
                    )}
                  </Space>

                  {!file && (
                    <p>
                      {t("app:admin.invoice_page_image_replace.no_pdf_hint")}
                    </p>
                  )}

                  <div
                    style={{
                      width: "100%",
                      maxHeight: "50vh",
                      overflowY: "auto",
                    }}
                  >
                    <canvas
                      ref={previewContainerRef}
                      style={{ width: "100%" }}
                      width={0}
                      height={0}
                    />
                  </div>
                </div>
              ),
            },
            {
              key: "image",
              label: (
                <span>
                  <Icon icon="fa:image" />
                  {t("app:admin.invoice_page_image_replace.tab_image")}
                </span>
              ),
              children: (
                <div>
                  <Upload
                    fileList={[]}
                    accept="image/*"
                    beforeUpload={(file) => {
                      // Clean up previous preview URL
                      if (imagePreviewUrl) {
                        URL.revokeObjectURL(imagePreviewUrl);
                      }
                      // Create new preview URL
                      const newPreviewUrl = URL.createObjectURL(file);
                      setImagePreviewUrl(newPreviewUrl);
                      setImageFile(file);
                      return false;
                    }}
                    showUploadList={false}
                  >
                    <Button
                      icon={<Icon icon="fa:image" />}
                      children={
                        imageFile
                          ? t(
                              "app:admin.invoice_page_image_replace.action_upload_new_image"
                            )
                          : t(
                              "app:admin.invoice_page_image_replace.action_upload_image"
                            )
                      }
                    />
                  </Upload>

                  {!imageFile && (
                    <p style={{ marginTop: 16 }}>
                      {t("app:admin.invoice_page_image_replace.no_image_hint")}
                    </p>
                  )}

                  {imageFile && imagePreviewUrl && (
                    <div style={{ marginTop: 16 }}>
                      <p>
                        {t(
                          "app:admin.invoice_page_image_replace.image_selected"
                        )}
                        : {imageFile.name}
                      </p>
                      <img
                        src={imagePreviewUrl}
                        alt="Preview"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "400px",
                          border: "1px solid #d9d9d9",
                        }}
                      />
                    </div>
                  )}
                </div>
              ),
            },
          ]}
        />
      </Modal>
    );
  }
);
